/* eslint-disable no-param-reassign */
import { createSelector } from 'reselect';
import { getProducts } from 'redux/selectors/product.selector';
import { getTotalPriceFromQuantity } from 'helpers/prices';

const getCartItems = (state) => state.cart.items;
const getCartProducts = (state) => state.cart.products;
const getMostSelledProducts = (state) => state.product.mostSelled;
const getNewArrivals = (state) => state.product.newArrivals;
const getOffers = (state) => state.product.offers;
const getSearchProducts = (state) => state.product.search;
const getSingleProduct = (state) => state.product.single;
const getCartItemsSub = (state) => state.cart.itemsSub;
const getItem = (state, props) => props.item;

// The cart hydrates its own products after a refresh, while products added during
// the current visit can still live only in a home/search/single product collection.
const getAvailableCartProducts = createSelector(
  [
    getCartProducts,
    getProducts,
    getMostSelledProducts,
    getNewArrivals,
    getOffers,
    getSearchProducts,
    getSingleProduct,
  ],
  (...sources) => {
    const productsById = new Map();
    sources.forEach((source) => {
      const products = Array.isArray(source) ? source : (source ? [source] : []);
      products.forEach((product) => {
        if (product?.id != null && !productsById.has(String(product.id))) {
          productsById.set(String(product.id), product);
        }
      });
    });
    return Array.from(productsById.values());
  },
);

export const itemQuantitySelector = createSelector([getCartItems, getItem],
  (cartItems, item) => (
    cartItems.find((cartItem) => cartItem.itemId === item.id, null)
  ));

export const itemsQuantitySelector = createSelector([getCartItems, getItem],
  (cartItems, item) => (
    cartItems.filter((cartItem) => cartItem.itemId === item.id, null)
  ));

export const itemsSubQuantitySelector = createSelector([getCartItemsSub, getItem],
  (cartItems, item) => (
    cartItems.filter((cartItem) => cartItem.itemId === item.id, null)
  ));

export const cartItemIdsSelector = createSelector([getCartItems], (items) => (
  items.map(({ itemId }) => itemId)
));

export const cartItemsSelector = createSelector([getCartItems, getCartProducts],
  (cartItems, products) => {
    const items = {};
    cartItems.forEach(({
      itemId, quantity, descriptionId, wholesale,
    }) => {
      const item = products.find((product) => product.id === itemId, null);
      if (item) {
        if (item?.usoMetros) {
          items[itemId] = {
            item,
            itemId,
            quantity,
            wholesale: wholesale ?? null,
            // colors: item.stock_product_descriptions.filter(desc => desc?.stock_descriptions?.stock_category_id == 1) || [],
            descriptionId: descriptionId ?? null,
            subItems: undefined,
            subItemsDescriptions: undefined,
            talles: undefined,
          };
          return;
        }
        if (item.stock_product_descriptions.length > 0) {
          const colors = item.stock_product_descriptions.filter(
            ({ stock_descriptions }) => stock_descriptions.stock_category_id === 1,
          );
          let talles = item.stock_product_descriptions.filter(
            ({ stock_descriptions }) => stock_descriptions.stock_category_id === 2,
          );

          let cartItem = items[itemId];
          if (!cartItem) {
            cartItem = {
              item,
              itemId,
              quantity,
              colors,
              subItems: [],
              subItemsDescriptions: {},
              talles,
            };
          }

          const description = item.stock_product_descriptions.find(
            ({ id }) => id === descriptionId,
          );
          const subItemsDescriptions = cartItems.find(
            (data) => (
              data.descriptionId === descriptionId && data.wholesale?.id === wholesale?.id
            ),
          );
          const othersSubItemsWholesale = cartItems.filter(
            (data) => (
              data.descriptionId === descriptionId && data.wholesale?.id !== wholesale?.id
            ),
          );

          subItemsDescriptions.quantity = quantity;
          subItemsDescriptions.description = description;
          const quantitiesAndDescriptions = othersSubItemsWholesale.map((otherProduct) => ({
            quantity: otherProduct.quantity,
            description: otherProduct.description,
            wholesale: otherProduct.wholesale,
          }));
          subItemsDescriptions.othersSubItemsWholesale = quantitiesAndDescriptions;

          if (!cartItem.subItemsDescriptions[wholesale?.id]) {
            cartItem.subItemsDescriptions[wholesale?.id] = {};
          }
          if (!cartItem.subItemsDescriptions[wholesale?.id][descriptionId]) {
            cartItem.subItemsDescriptions[wholesale?.id][descriptionId] = [];
          }
          cartItem.subItemsDescriptions[wholesale?.id][descriptionId].push(subItemsDescriptions);
          items[itemId] = cartItem;
        } else if (item.product_wholesaler.length > 0) {
          let cartItem = items[itemId];
          if (!cartItem) {
            cartItem = {
              item,
              itemId,
              quantity,
              colors: item.stock_product_descriptions.filter(desc => desc?.stock_descriptions?.stock_category_id == 1) || [],
              subItems: [],
              subItemsDescriptions: undefined,
              talles: undefined,
            };
          }
          const subItems = cartItems.find((data) => (data.itemId === itemId
             && data.wholesale?.id === wholesale?.id));
          subItems.quantity = quantity;
          cartItem.subItems = [...cartItem.subItems, subItems];

          items[itemId] = cartItem;
        } else {
          items[itemId] = {
            item,
            itemId,
            quantity,
            subItems: undefined,
            colors: undefined,
            subItemsDescriptions: undefined,
            talles: undefined,
          };
        }
      }
    });

    const array = Object.values(items).map(({
      item, quantity, colors, subItems,
      subItemsDescriptions, talles,
    }) => {
      if ((colors && !Object.values(subItemsDescriptions || {}).every((value) => Array.isArray(value)
      && value.length === 0))) {
        const cartQuantity = Object.values(subItemsDescriptions || {})
          .map((obj) => obj.quantity)
          .reduce((total, q) => total + q, 0);

        const combinations = colors?.reduce((a, b) => {
          a[b.stock_description_id] = a[b.stock_description_id] || {};
          const combination1 = item?.stock_product_descriptions
            ?.filter(({ stock_descriptions }) => stock_descriptions.stock_category_id !== 1)
            ?.find(({ combination }) => combination === b.combination);
          const combination2 = item?.stock_product_descriptions
            ?.filter(({ stock_descriptions }) => stock_descriptions.stock_category_id === 1)
            ?.find(({ combination }) => combination === b.combination);
          const descriptionId = (combination1 || combination2)?.id;

          Object.entries(subItemsDescriptions).forEach(([wholesaleId, subItem]) => {
            if (subItem[descriptionId] && Array.isArray(subItem[descriptionId])) {
              a[wholesaleId] = a[wholesaleId] || {};
              a[wholesaleId][b.stock_description_id] = a[wholesaleId][b.stock_description_id] || [];
              a[wholesaleId][b.stock_description_id].push(...subItem[descriptionId]);
            } else if (!a[wholesaleId]) {
              a[wholesaleId] = {};
            }
          });

          return a;
        }, Object.create(null));

        // Filtra los talles para incluir solo los que están en subItemsDescriptions
        talles = talles?.filter((talle) => {
          return Object.values(subItemsDescriptions || {}).some((subItemGroup) => {
            return Object.keys(subItemGroup).some((key) =>
              subItemGroup[key].some((subItem) => subItem.descriptionId === talle.id)
            );
          });
        });

        return {
          item,
          itemId: item.id,
          quantity: cartQuantity,
          colors,
          subItems: undefined,
          subItemsDescriptions: combinations,
          talles,
        };
      }

      // Filtra los talles para incluir solo los que están en subItemsDescriptions
      talles = talles?.filter((talle) => {
        return Object.values(subItemsDescriptions || {}).some((subItemGroup) => {
          return Object.keys(subItemGroup).some((key) =>
            subItemGroup[key].some((subItem) => subItem.descriptionId === talle.id)
          );
        });
      });

      return {
        item,
        itemId: item.id,
        quantity,
        colors,
        subItems,
        subItemsDescriptions,
        talles,
      };
    });

    return array;
  });

export const cartSizeSelector = createSelector([getCartItems], (items) => (
  items.reduce((a, b) => a + b.quantity, 0)
));

export const cartSubtotalSelector = createSelector([getAvailableCartProducts, getCartItems],
  (products, cartItems) => {
    const total = cartItems.reduce((a, b) => {
      const cartItem = products.find((product) => product.id === b.itemId, null);
      let price = 0;
      
      if (cartItem) {
        // Prioridad 1: Wholesale
        if (b.wholesale?.bool && cartItem.product_wholesaler.length > 0) {
          const subItem = cartItem.product_wholesaler.find((prod) => prod.id === b.wholesale.id);
          price = subItem.price * b.quantity;
        } else {
          // Prioridad 2: Sale
          price = cartItem.sale ? cartItem.sale * b.quantity : cartItem.price * b.quantity;

          // Prioridad 3: Stock Description
          if (b.descriptionId != null) {
            const cartProductStockDescription = cartItem.stock_product_descriptions.find(
              (stock_product_description) => stock_product_description.id === b.descriptionId,
            );

            // Si hay un precio específico en stock, se usa ese precio.
            if (cartProductStockDescription?.price > 0) {
              price = cartProductStockDescription.price * b.quantity;
            }
          }
        }
      }

      return a + price;
    }, 0);
    
    return total;
  });

  export const cartSubtotalTextilSelector = createSelector(
    [getAvailableCartProducts, getCartItems],
    (products, cartItems) => {
      const groupedItems = cartItems.reduce((acc, current) => {
        if (!acc[current.itemId]) acc[current.itemId] = [];
        acc[current.itemId].push(current);
        return acc;
      }, {});
  
      const total = Object.keys(groupedItems).reduce((acc, productId) => {
        const product = products.find((p) => p.id === Number(productId));
        if (!product) return acc;
  
        const itemsOfProduct = groupedItems[productId];
        
        const subtotalGroup = itemsOfProduct.reduce((subAcc, cartItem) => {
          let price = product.sale > 0 ? product.sale : product.price;
  
          if (cartItem.descriptionId != null) {
            const variant = product.stock_product_descriptions.find(
              (spd) => spd.id === cartItem.descriptionId
            );
            
            if (variant?.price > 0) {
              price = variant.price;
            }
          }
  
          const itemTotal = getTotalPriceFromQuantity(
            cartItem.quantity,
            price,
            product.stock_product_descriptions
          );
  
          return subAcc + itemTotal;
        }, 0);
  
        return acc + subtotalGroup;
      }, 0);
  
      return total;
    }
  );
